import obtenerParametrosLegacy from '../../Parametros';
import obtenerDescripcionesLegacy from '../../Descripciones';
import obtenerPatenteLegacy from '../../Patente';
import obtenerMarcasLegacy from '../../Marcas';
import obtenerModelosLegacy from '../../Modelos';
import obtenerAniosLegacy from '../../Anios';
import obtenerVersionesLegacy from '../../Versiones';
import obtenerAniosPorVersionLegacy from '../../AniosByVersion';
import obtenerProvinciasLegacy from '../../Provincias';
import obtenerLocalidadesLegacy from '../../Localidades';
import obtenerBancosLegacy from '../../Bancos';
import obtenerTiposTarjetaLegacy from '../../TiposTarjeta';
import { obtenerCondicionesIVA as obtenerCondicionesIvaLegacy } from '../../CondicionesIVA';
import { updateEmisionStep as updateEmisionStepLegacy } from '../../updateEmisionStep';
import subirCertificadoLegacy from '../../CertificadoNoRodamiento';
import descargarPolizaLegacy from '../../DescargarPoliza';

import { isAutoViaBff, marcarSesionLegacy } from './flag';
import * as bff from './endpoints';
import {
  adaptAnios,
  adaptCatalogo,
  adaptDescripciones,
  adaptParametros,
  adaptPatente,
  adaptProvincias,
  aNombres,
  ParametrosLegacy,
} from './adapters';
import {
  idDeMarca,
  idDeModelo,
  idDeVersion,
  idsDePatente,
  registrarMarcas,
  registrarModelos,
  registrarPatente,
  registrarVersiones,
} from './catalogoVehiculos';
import { registrarProvincias } from './catalogoProvincias';
import { aBffError } from './bffError';
import { leerSesionCotizacion } from './quoteSession';
import { PasoEmision, Ramo, TipoDocumento } from './types';
import {
  advertenciasDe,
  EmbudoAdvertencia,
  EmbudoRechazo,
  isEmbudoEol,
  PasoEmbudo,
  rechazoDelEmbudo,
} from './embudo';
import { publicarAvisosEmbudo } from './avisosEmbudo';
import { getRubroFromPath } from '../../../../utils/getRubro';

/**
 * FACHADA del cotizador de auto/moto.
 *
 * Cada función tiene la MISMA firma y devuelve el MISMO shape que el servicio
 * viejo equivalente, y adentro decide el camino:
 *
 *     if (await isAutoViaBff()) -> BFF (+ adaptador)
 *     else                      -> monolito (servicio viejo, intacto)
 *
 * Así el flag se enciende y se apaga sin tocar las pantallas. Las tres cosas
 * que sí cambian de forma (precios por SSE, emisión y documentos) se exportan
 * además con su shape nuevo, para que la pantalla elija.
 */

/* ───────────────── helpers ───────────────── */

/** "AUTO" | "Auto" | "auto" -> "auto"; cualquier variante de moto -> "moto". */
export const aRamo = (rubro?: string): Ramo =>
  (rubro ?? getRubroFromPath()).toLowerCase().startsWith('moto')
    ? 'moto'
    : 'auto';

const nombreDe = (opcion: unknown): string | undefined => {
  if (!opcion) return undefined;
  if (typeof opcion === 'string') return opcion;
  const valor = (opcion as { value?: string; label?: string }).value;
  return valor ?? (opcion as { label?: string }).label;
};

const anioNumerico = (valor?: string): number => {
  if (!valor) return 0;
  if (valor.toLowerCase().includes('0 km')) return new Date().getFullYear();
  const parseado = parseInt(valor, 10);
  return Number.isNaN(parseado) ? 0 : parseado;
};

/* ───────────────── §3.1 parámetros y descripciones ───────────────── */

/**
 * Bootstrap del cotizador. Mantiene el envelope de axios (`{data}`) porque los
 * dos `Cotizador.tsx` leen `response.data`.
 *
 * Si el BFF falla acá (404/5xx/red) la sesión se degrada a legacy y se
 * responde con el camino viejo: es el fallback del deliverable 10.
 */
export const obtenerParametros = async ({
  rubro = 'AUTO',
}: {
  rubro?: string;
}): Promise<{ data: ParametrosLegacy | unknown }> => {
  if (await isAutoViaBff()) {
    const ramo = aRamo(rubro);
    try {
      const respuesta = await bff.getParametros(ramo);
      return {
        data: adaptParametros(respuesta, ramo === 'moto' ? 'MOTO' : 'AUTO'),
      };
    } catch (error) {
      marcarSesionLegacy(aBffError(error).code);
    }
  }
  return obtenerParametrosLegacy({ rubro });
};

/**
 * Las descripciones de cobertura vienen dentro de `GET parametros` en el BFF:
 * no hay un endpoint aparte. Se mantiene la firma vieja y el envelope
 * `{data:{api_data}}` que lee el Cotizador.
 */
export const obtenerDescripciones = async (
  rubro: string,
  origen: string,
  tipoCotizador: string
): Promise<{ data: { api_data: unknown } } | unknown> => {
  if (await isAutoViaBff()) {
    try {
      const respuesta = await bff.getParametros(aRamo(rubro));
      return { data: { api_data: adaptDescripciones(respuesta.descripciones) } };
    } catch (error) {
      marcarSesionLegacy(aBffError(error).code);
    }
  }
  return obtenerDescripcionesLegacy(rubro, origen, tipoCotizador);
};

/* ───────────────── §3.1 patente ───────────────── */

/**
 * Datacar por patente. El recaptcha del camino nuevo se mintea adentro del
 * cliente (acción `patente`): el token que recibe como segundo argumento es
 * el del camino viejo y se ignora con el flag prendido.
 */
export const obtenerPatente = async (
  patente: string,
  tokenRecaptchaLegacy: string
): Promise<unknown> => {
  if (await isAutoViaBff()) {
    try {
      const respuesta = await bff.postPatente(patente);
      const vehiculo = respuesta?.vehiculo;
      // Los ids se resuelven por nombre y pueden venir null: solo se registran
      // si están los tres, para no dejar una cascada de catálogos a medias.
      if (vehiculo?.marcaId && vehiculo.modeloId && vehiculo.versionId) {
        registrarPatente({
          marcaId: vehiculo.marcaId,
          modeloId: vehiculo.modeloId,
          versionId: vehiculo.versionId,
        });
      }
      return adaptPatente(respuesta);
    } catch (error) {
      // Igual que el camino viejo: sin datos se sigue a mano, no se corta.
      // eslint-disable-next-line no-console
      console.warn('[auto_via_bff] patente:', aBffError(error).code);
      return null;
    }
  }
  return obtenerPatenteLegacy(patente, tokenRecaptchaLegacy);
};

/* ───────────────── §3.1 catálogo de vehículos ───────────────── */

export const obtenerMarcas = async (rubro = 'Auto'): Promise<string[]> => {
  if (await isAutoViaBff()) {
    const items = await bff.getMarcas(aRamo(rubro));
    registrarMarcas(items);
    return aNombres(items);
  }
  return obtenerMarcasLegacy(rubro);
};

export const obtenerModelos = async (
  marca: unknown,
  rubro = 'auto'
): Promise<string[]> => {
  if (await isAutoViaBff()) {
    const marcaId = idDeMarca(nombreDe(marca));
    if (!marcaId) return [];
    const items = await bff.getModelos(aRamo(rubro), marcaId);
    registrarModelos(items);
    return aNombres(items);
  }
  return obtenerModelosLegacy(marca, rubro);
};

export const obtenerAnios = async (
  marca: unknown,
  modelo: unknown,
  rubro = 'auto'
): Promise<string[]> => {
  if (await isAutoViaBff()) {
    const marcaId = idDeMarca(nombreDe(marca));
    const modeloId = idDeModelo(nombreDe(modelo));
    if (!marcaId || !modeloId) return [];
    return adaptAnios(await bff.getAnios(aRamo(rubro), marcaId, modeloId));
  }
  return obtenerAniosLegacy(marca, modelo, rubro);
};

export const obtenerVersiones = async (
  marca: unknown,
  modelo: unknown,
  anio: unknown,
  rubro = 'auto'
): Promise<string[]> => {
  if (await isAutoViaBff()) {
    const marcaId = idDeMarca(nombreDe(marca));
    const modeloId = idDeModelo(nombreDe(modelo));
    const anioNum = anioNumerico(nombreDe(anio));
    if (!marcaId || !modeloId || !anioNum) return [];
    const items = await bff.getVersiones(
      aRamo(rubro),
      marcaId,
      modeloId,
      anioNum
    );
    registrarVersiones(items);
    return aNombres(items);
  }
  return obtenerVersionesLegacy(marca, modelo, anio, rubro);
};

/**
 * Años disponibles para el vehículo que resolvió el datacar.
 *
 * El BFF no tiene un `anios-by-version`: los años se piden por marca y modelo
 * (§3.1), usando los ids que dejó `POST patente`.
 */
export const obtenerAniosPorVersion = async (
  marca: string,
  version: string,
  rubro = 'Auto'
): Promise<string[]> => {
  if (await isAutoViaBff()) {
    const ids = idsDePatente();
    const marcaId = ids?.marcaId || idDeMarca(marca) || 0;
    const modeloId = ids?.modeloId || 0;
    const versionId = ids?.versionId || idDeVersion(version) || undefined;
    if (!marcaId || !modeloId) return [];
    return adaptAnios(
      await bff.getAnios(aRamo(rubro), marcaId, modeloId, versionId)
    );
  }
  return obtenerAniosPorVersionLegacy(marca, version, rubro);
};

/* ───────────────── §3.1 lookups ───────────────── */

export const obtenerProvincias = async (): Promise<string[]> => {
  if (await isAutoViaBff()) {
    const provincias = await bff.getProvincias('auto');
    registrarProvincias(provincias);
    return adaptProvincias(provincias);
  }
  return obtenerProvinciasLegacy();
};

/**
 * Segucore no resuelve localidad a partir de la provincia (§3.1): con el flag
 * prendido no hay catálogo de localidades y la pantalla la pide como texto
 * libre + código postal. Devolver [] es la señal para ese modo.
 */
export const obtenerLocalidades = async (
  provincia: string
): Promise<string[]> => {
  if (await isAutoViaBff()) return [];
  return obtenerLocalidadesLegacy(provincia);
};

/* ───────────────── §3.1 catálogos de pago ───────────────── */

export const obtenerBancos = async (
  rubro?: string
): Promise<Array<{ codigo: string; descripcion: string }>> => {
  if (await isAutoViaBff()) {
    return adaptCatalogo(await bff.getBancos(aRamo(rubro)));
  }
  return obtenerBancosLegacy();
};

export const obtenerTiposTarjeta = async (
  compania: string,
  rubro?: string
): Promise<Array<{ codigo: string; descripcion: string }>> => {
  if (await isAutoViaBff()) {
    const items = await bff.getTiposTarjeta(aRamo(rubro), compania);
    // El catálogo puede venir con tipos de otras compañías: se filtra acá para
    // que el select solo ofrezca los que la compañía elegida acepta.
    const deLaCompania = compania
      ? items.filter(
          (item) =>
            !item.codigoCompania ||
            item.codigoCompania.toLowerCase() === compania.toLowerCase()
        )
      : items;
    return adaptCatalogo(deLaCompania);
  }
  return obtenerTiposTarjetaLegacy(compania);
};

/** El shape viejo de condiciones de IVA es `{value,label}` (no `{codigo,...}`). */
export const obtenerCondicionesIVA = async (
  compania = '',
  rubro?: string
): Promise<Array<{ value: string; label: string }>> => {
  if (await isAutoViaBff()) {
    const items = await bff.getCondicionesIva(aRamo(rubro), compania);
    return adaptCatalogo(items).map((item) => ({
      value: item.codigo,
      label: item.descripcion,
    }));
  }
  return obtenerCondicionesIvaLegacy();
};

export const obtenerTiposInspeccion = async (
  compania: string,
  anio: number,
  esCeroKm: boolean,
  rubro?: string
): Promise<Array<{ codigo: string; descripcion: string }>> => {
  if (!(await isAutoViaBff())) return [];
  return adaptCatalogo(
    await bff.getTiposInspeccion(aRamo(rubro), compania, anio, esCeroKm)
  );
};

/* ───────────────── §3.4 pasos de emisión ───────────────── */

/** step numérico del monolito -> paso nombrado del BFF (§3.4). */
export const pasoDeStep = (step: number | string): PasoEmision => {
  switch (String(step)) {
    case '0':
    case '1':
      return 'revision';
    case '2':
      return 'datos_personales';
    case '3':
      return 'radicacion';
    case '4':
      return 'vehiculo';
    default:
      return 'pago';
  }
};

/** Claves que NUNCA salen del front hacia `POST pasos` (§3.4). */
const CLAVES_DE_PAGO = [
  'NumeroTarjeta',
  'numeroTarjeta',
  'MedioPago',
  'TipoTarjetaCredito',
  'BancoTarjetaCredito',
  'NombreTitularTarjeta',
  'ApellidoTitularTarjeta',
  'MesVencimientoTarjeta',
  'AnioVencimientoTarjeta',
  'cvc',
  'cbu',
];

export const sinDatosDePago = (
  datos: Record<string, unknown>
): Record<string, unknown> => {
  const limpio: Record<string, unknown> = {};
  Object.entries(datos || {}).forEach(([clave, valor]) => {
    if (!CLAVES_DE_PAGO.includes(clave)) limpio[clave] = valor;
  });
  return limpio;
};

/**
 * Tracking de paso. Con el flag prendido va a `POST pasos` del BFF; el `step`
 * numérico se traduce al paso nombrado y se filtran los datos de pago.
 *
 * Nunca tira: el tracking de abandono no puede romper el funnel.
 */
export const updateEmisionStep = async (
  step: number | string,
  datos: Record<string, unknown>,
  rubro?: string
): Promise<unknown> => {
  if (await isAutoViaBff()) {
    const { cotizacionId } = leerSesionCotizacion();
    if (!cotizacionId) return undefined;
    try {
      await bff.postPaso(
        aRamo(rubro),
        cotizacionId,
        pasoDeStep(step),
        sinDatosDePago(datos)
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[auto_via_bff] pasos:', aBffError(error).code);
    }
    return undefined;
  }
  return updateEmisionStepLegacy(step as never, datos as never);
};

/**
 * Como termino un paso de emision para la pantalla: sigue, o frena con el
 * rechazo del embudo (422) para marcar los campos y mostrar el mensaje.
 */
export type ResultadoPaso =
  | { frena: false; advertencias: EmbudoAdvertencia[] }
  | { frena: true; rechazo: EmbudoRechazo };

const SIGUE: ResultadoPaso = { frena: false, advertencias: [] };

/**
 * Paso de emision con el embudo web EOL (US 23704). Embudo apagado: hace
 * exactamente lo de `updateEmisionStep` con los datos del monolito. Prendido:
 * manda el paso de la wiki 26 (`titular`, `radicacion`, `vehiculo`, `pago`)
 * con sus claves, porque el BFF descarta las que la wiki no declara.
 *
 * `datosLegacy` null: el paso no existia antes del embudo (`pago`); con el
 * embudo apagado no se manda nada.
 */
export const informarPaso = async (
  step: number | string,
  datosLegacy: Record<string, unknown> | null,
  rubro: string | undefined,
  embudo: { paso: PasoEmbudo; datos: object }
): Promise<ResultadoPaso> => {
  if (!(await isEmbudoEol())) {
    if (datosLegacy) await updateEmisionStep(step, datosLegacy, rubro);
    return SIGUE;
  }
  const { cotizacionId } = leerSesionCotizacion();
  if (!cotizacionId) return SIGUE;
  try {
    const respuesta = await bff.postPaso(
      aRamo(rubro),
      cotizacionId,
      embudo.paso,
      embudo.datos as Record<string, unknown>
    );
    // 200: las advertencias no frenan; la pantalla siguiente las muestra.
    const advertencias = advertenciasDe(respuesta);
    publicarAvisosEmbudo(advertencias);
    return { frena: false, advertencias };
  } catch (error) {
    publicarAvisosEmbudo([]);
    const bffError = aBffError(error);
    const rechazo = rechazoDelEmbudo(bffError);
    if (rechazo) return { frena: true, rechazo };
    // 500, timeout o red: el BFF deja el paso en el outbox y reintenta. No frena.
    // eslint-disable-next-line no-console
    console.warn('[embudo_eol] pasos:', bffError.code);
  }
  return SIGUE;
};

/* ───────────────── §3.6 documentos ───────────────── */

/**
 * Certificado de no rodamiento. Con el flag prendido el id es el `polizaId`
 * opaco del BFF; con el flag apagado sigue siendo el id numérico del monolito.
 */
export const subirCertificadoNoRodamiento = async (
  nombre: string,
  extension: string,
  dataBase64: string,
  idPoliza: number | string,
  rubro?: string
): Promise<{ api_status: number }> => {
  if (await isAutoViaBff()) {
    await bff.postCertificadoNoRodamiento(aRamo(rubro), String(idPoliza), {
      nombre,
      extension,
      dataBase64,
    });
    return { api_status: 200 };
  }
  return subirCertificadoLegacy(nombre, extension, dataBase64, Number(idPoliza));
};

/** Documento de póliza como Blob (el camino viejo devolvía base64 en JSON). */
export const descargarDocumento = (
  polizaId: string,
  kind: TipoDocumento,
  rubro?: string
): Promise<Blob> => bff.getDocumento(aRamo(rubro), polizaId, kind);

/** Camino viejo de impresión, intacto. Solo se usa con el flag apagado. */
export const descargarPoliza = descargarPolizaLegacy;

/* ───────────────── re-exports de la capa nueva ───────────────── */

export { isAutoViaBff, marcarSesionLegacy, esSesionLegacy } from './flag';
export { BffError, aBffError } from './bffError';
export {
  mensajeDeError,
  mensajesPorCampo,
  requiereVolverADatos,
  MENSAJES_ERROR,
} from './errorMessages';
export * from './adapters';
export { leerSesionCotizacion, guardarSesionCotizacion } from './quoteSession';
export { bff };
